import { SceneManager } from '../sceneManager'
import { isSelectableMesh, isSketchMesh, isWallMesh } from '../utils'
import store from '../../store'
import { setSelectedElementsIds } from '../../actions/construction'
import { AbstractMesh, Color4, PointerEventTypes } from '@babylonjs/core'
import { SelectionArea } from './selectionArea'

type SelectionOptions = {
  elementsIds?: string[]
  silent?: boolean
}

type DeselectionOptions = SelectionOptions

export class SelectionManager {
  private static instance: SelectionManager | null
  private sceneManager: SceneManager
  private selectedElementsIds: Set<string>
  private selectionAreaUI: SelectionArea | null = null

  private constructor() {
    this.sceneManager = SceneManager.getInstance()
    this.selectedElementsIds = new Set()
    const scene = this.sceneManager.getScene()
    const camera = this.sceneManager.getCamera()
    scene?.onBeforeRenderObservable.add(() => {
      const width = camera && camera.radius ? camera.radius / 5 : 100
      scene.meshes
        .filter((mesh) => mesh.edgesRenderer)
        .forEach((mesh) => (mesh.edgesWidth = width))
    })
  }

  static getInstance(): SelectionManager {
    if (!SelectionManager.instance) {
      SelectionManager.instance = new SelectionManager()
    }

    return SelectionManager.instance
  }

  isSelected(elementId: string): boolean {
    return this.selectedElementsIds.has(elementId)
  }

  get selectedCount(): number {
    return this.selectedElementsIds.size
  }

  select(options?: SelectionOptions) {
    this.deselect({ silent: true })

    const { elementsIds = null, silent = false } = options || {}
    const selectAllElements = !Boolean(elementsIds)
    const elementsIdsToSelect = new Set(elementsIds)

    //const materialManager = MaterialManager.getInstance()
    this.sceneManager.getScene()?.meshes.forEach((mesh) => {
      if (
        isSelectableMesh(mesh) &&
        (selectAllElements || elementsIdsToSelect.has(mesh.id))
      ) {
        this.selectedElementsIds.add(mesh.id)

        //mesh.material = materialManager.getHighlightMaterial(mesh.metadata.type)
        if (mesh.geometry) {
          mesh.enableEdgesRendering(0.9999)
          mesh.edgesWidth = 100
          mesh.edgesColor = Color4.FromHexString('#1705E5')
        } else {
          mesh
            .getChildMeshes()
            .filter((m) => m.geometry && !isSketchMesh(m))
            .forEach((mesh) => {
              mesh.enableEdgesRendering(0.9999)
              mesh.edgesWidth = 100
              mesh.edgesColor = Color4.FromHexString('#1705E5')
            })
        }
        mesh.metadata.isSelected = true
      }
    })

    if (silent) {
      return
    }

    store.dispatch(setSelectedElementsIds(Array.from(this.selectedElementsIds)))
  }

  deselect(options?: DeselectionOptions) {
    const { elementsIds = null, silent = false } = options || {}
    const deselectAllElements = !Boolean(elementsIds)
    const elementsIdsToDeselect = new Set(elementsIds)

    //const materialManager = MaterialManager.getInstance()
    this.sceneManager.getScene()?.meshes.forEach((mesh) => {
      if (
        isSelectableMesh(mesh) &&
        (deselectAllElements || elementsIdsToDeselect.has(mesh.id))
      ) {
        this.selectedElementsIds.delete(mesh.id)

        /*mesh.material = mesh.metadata.isTransparent
          ? materialManager.getInactiveMaterial(mesh.metadata.type)
          : materialManager.getNormalMaterial(mesh.metadata.type)*/
        if (mesh.geometry) {
          if (mesh.metadata.edgesColor) {
            mesh.edgesColor = mesh.metadata.edgesColor
          } else {
            mesh.disableEdgesRendering()
          }
        } else {
          mesh
            .getChildMeshes()
            .filter((m) => m.geometry && !isSketchMesh(m))
            .forEach((m) => {
              if (m.metadata && m.metadata.edgesColor) {
                mesh.edgesColor = mesh.metadata.edgesColor
              } else {
                m.disableEdgesRendering()
              }
            })
        }

        mesh.metadata.isSelected = false
      }
    })

    if (silent) {
      return
    }

    store.dispatch(setSelectedElementsIds(Array.from(this.selectedElementsIds)))
  }

  clearCodeValidationMetadata() {
    this.sceneManager.getScene()?.meshes.forEach((mesh) => {
      if (mesh.metadata?.codeValidationStatus) {
        delete mesh.metadata.codeValidationStatus
        delete mesh.metadata.codeValidationDiscipline
      }
    })
  }

  updatePickingList() {
    this.selectionAreaUI?.updatePickingList()
  }

  enableMarqueeSelection() {
    const scene = this.sceneManager.getScene()
    if (!scene) {
      return
    }

    if (this.selectionAreaUI) {
      this.selectionAreaUI.dispose()
    }

    let firstTwoPickedWallMeshes: AbstractMesh[] = []
    this.selectionAreaUI = new SelectionArea(
      scene,
      (eventType, pickedMeshes) => {
        const pickedWallMeshes = pickedMeshes.filter(isWallMesh)
        firstTwoPickedWallMeshes = firstTwoPickedWallMeshes.filter((m) =>
          pickedWallMeshes.includes(m),
        )

        if (firstTwoPickedWallMeshes.length < 2) {
          const notAlreadyPickedWallMeshes = pickedWallMeshes.filter(
            (m) => !firstTwoPickedWallMeshes.includes(m),
          )

          for (let i = firstTwoPickedWallMeshes.length; i < 2; i += 1) {
            const newMesh = notAlreadyPickedWallMeshes.shift()
            if (newMesh) {
              firstTwoPickedWallMeshes.push(newMesh)
            } else {
              break
            }
          }
        }

        this.select({
          elementsIds: firstTwoPickedWallMeshes.map((m) => m.id),
          silent: eventType === PointerEventTypes.POINTERMOVE,
        })
      },
    )
  }

  disableMarqueeSelection() {
    this.selectionAreaUI?.dispose()
    this.selectionAreaUI = null
  }

  dispose() {
    this.selectionAreaUI?.dispose()
    this.selectionAreaUI = null
    SelectionManager.instance = null
  }
}
